'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

interface VoiceMessageRecorderProps {
  onSend: (blob: Blob, durationSec: number) => void;
  onCancel: () => void;
}

export default function VoiceMessageRecorder({ onSend, onCancel }: VoiceMessageRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [amplitude, setAmplitude] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up analyser for amplitude visualization
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm' :'audio/ogg';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(100);
      setIsRecording(true);
      startTimeRef.current = Date.now();
      setElapsed(0);

      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 500);

      // Amplitude animation
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAmplitude(Math.min(avg / 128, 1));
        animFrameRef.current = requestAnimationFrame(tick);
      };
      animFrameRef.current = requestAnimationFrame(tick);
    } catch {
      // Microphone permission denied or unavailable
      onCancel();
    }
  }, [onCancel]);

  const stopAndSend = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;

    const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
      if (blob.size > 0 && duration >= 1) {
        onSend(blob, duration);
      } else {
        onCancel();
      }
    };

    recorder.stop();
    cleanup();
  }, [onSend, onCancel]);

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setIsRecording(false);
    setAmplitude(0);
  };

  const handleCancel = useCallback(() => {
    mediaRecorderRef.current?.stop();
    cleanup();
    onCancel();
  }, [onCancel]);

  useEffect(() => {
    startRecording();
    return () => {
      cleanup();
    };
  }, []);

  const scale = 1 + amplitude * 0.35;

  return (
    <div
      className="flex items-center gap-3 px-3 py-3 w-full"
      style={{ background: 'rgba(16,28,41,0.97)' }}
    >
      {/* Cancel button */}
      <button
        onClick={handleCancel}
        className="flex-shrink-0 w-[44px] h-[44px] rounded-full flex items-center justify-center transition-all active:scale-90"
        style={{ background: 'rgba(255,80,80,0.15)' }}
        aria-label="Cancel recording"
      >
        <X size={20} style={{ color: '#FF5050' }} />
      </button>

      {/* Waveform + timer */}
      <div
        className="flex-1 flex items-center gap-3 px-4 py-2.5 min-h-[50px]"
        style={{
          background: '#1B2A3A',
          borderRadius: '26px',
          border: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {/* Pulsing mic dot */}
        <div
          className="flex-shrink-0 w-2.5 h-2.5 rounded-full"
          style={{
            background: '#FF4444',
            boxShadow: `0 0 ${6 + amplitude * 10}px rgba(255,68,68,0.8)`,
            transition: 'box-shadow 0.1s',
          }}
        />

        {/* Animated waveform bars */}
        <div className="flex items-center gap-[3px] flex-1">
          {Array.from({ length: 28 }).map((_, i) => {
            const phase = (i / 28) * Math.PI * 2;
            const h = isRecording
              ? 4 + amplitude * 20 * Math.abs(Math.sin(phase + elapsed * 0.5))
              : 4;
            return (
              <div
                key={i}
                className="rounded-full flex-shrink-0"
                style={{
                  width: '2px',
                  height: `${Math.max(4, h)}px`,
                  background: '#2EA6FF',
                  opacity: 0.5 + amplitude * 0.5,
                  transition: 'height 0.08s ease',
                }}
              />
            );
          })}
        </div>

        {/* Timer */}
        <span
          className="flex-shrink-0 text-[13px] font-mono font-medium tabular-nums"
          style={{ color: '#FF4444', minWidth: '38px', textAlign: 'right' }}
        >
          {formatTime(elapsed)}
        </span>
      </div>

      {/* Send button */}
      <button
        onClick={stopAndSend}
        className="flex-shrink-0 w-[44px] h-[44px] sm:w-[54px] sm:h-[54px] rounded-full flex items-center justify-center transition-all duration-200 active:scale-90 shadow-lg"
        style={{
          background: '#2296E8',
          boxShadow: '0 4px 16px rgba(34,150,232,0.4)',
          transform: `scale(${scale})`,
          transition: 'transform 0.1s ease, box-shadow 0.1s ease',
        }}
        aria-label="Send voice message"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
        </svg>
      </button>
    </div>
  );
}
